<template>
    <div>
        <div class="p-d-flex p-flex-column">

            <Avatar class="p-as-center" icon="pi pi-user" size="xlarge" shape="circle" />
            <div class="p-as-center p-text-bold">{{ user.name }}</div>

            <div class="p-fluid p-mt-4 p-px-1">

                <div class="p-d-flex p-jc-end p-mb-3">

                    <div class="p-d-flex gap-.5rem">
                        <Button
                            v-if="editMode"
                            class="p-button-success p-button-rounded p-mr-1"
                            icon="pi pi-check"
                            @click="displayEditConfirmation = true"
                            :title="$t('confirm')"
                        />

                        <Button
                            v-if="editMode"
                            class="p-button-danger p-button-rounded"
                            icon="pi pi-times"
                            @click="returnToReadMode"
                            :title="$t('cancel')"
                        />

                        <Button
                            v-if="!editMode && canEditUser"
                            class="p-button-success p-button-rounded"
                            icon="pi pi-pencil"
                            @click="editMode = true"
                            :title="$t('edit')"
                        />
                    </div>

                    <YesNoDialog :displayModal.sync="displayEditConfirmation" @on-yes="editUser" />
                </div>

                <div v-if="errors.length" class="p-d-flex p-flex-column mb-3">
                    <span v-for="error in errors" :key="error" class="text-danger font-italic">{{ error }}</span>
                </div>

                <div class="p-field p-grid">
                    <label for="username" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('username') }}</label>
                    <div class="p-col-6">

                        <InputText v-if="editMode" v-model="form.username" id="username" :placeholder="$t('username')" />
                        <div v-else class="p-py-2 p-pl-2">{{ user.username }}</div>
                    </div>
                </div>
                <div class="p-field p-grid">
                    <label for="name" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('name') }}</label>
                    <div class="p-col-6">
                        <InputText v-if="editMode" v-model="form.name" id="name" :placeholder="$t('name')" />
                        <div v-else class="p-py-2 p-pl-2">{{ user.name || '---' }}</div>
                    </div>
                </div>
                <div class="p-field p-grid">
                    <label for="email" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('email') }}</label>
                    <div class="p-col-6">
                        <InputText v-if="editMode" v-model="form.email" id="email" :placeholder="$t('email')" />
                        <div v-else class="p-py-2 p-pl-2">{{ user.email || '---' }}</div>
                    </div>
                </div>

                <div class="p-field p-grid">
                    <label for="sitePreferencesId" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('postLoginDestination') }}</label>
                    <div class="p-col-6">
                        <Dropdown
                            v-if="editMode"
                            v-model="form.sitePreferences"
                            :options="sitePreferencesOptions"
                            optionLabel="id"
                            :placeholder="$t('postLoginDestination')"
                        >
                            <template #value>{{ form.sitePreferences ? form.sitePreferences.name : '---' }}</template>

                            <template #option="slotProps">
                                <!-- hacky workaround to display labels as "optionLabel" throws error if not unique (as in this case name is) -->
                                {{  slotProps.option.name  }}
                            </template>
                        </Dropdown>
                        <div v-else class="p-py-2 p-pl-2">
                            {{ user.sitePreferences ? user.sitePreferences.name : '---' }}
                        </div>
                    </div>
                </div>

                <div class="p-field p-grid">
                    <label for="accessTemplateId" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('accessTemplate') }}</label>
                    <div class="p-col-6">
                        <Dropdown
                            v-if="editMode"
                            v-model="form.accessTemplate"
                            :options="accessTemplateOptions"
                            optionLabel="id"
                            :placeholder="$t('postLoginDestination')"
                        >
                            <template #value>{{ form.accessTemplate.name }}</template>

                            <template #option="slotProps">
                                <!-- hacky workaround to display labels as "optionLabel" throws error if not unique (as in this case name is) -->
                                {{  slotProps.option.name  }}
                            </template>
                        </Dropdown>
                        <div v-else class="p-py-2 p-pl-2">
                            {{ user.accessTemplate ? user.accessTemplate.name : '---' }}
                        </div>
                    </div>
                </div>

                <div class="p-field p-grid">
                    <label for="automatedMails" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('shouldReceiveAutomatedMails') }}</label>
                    <div class="p-col-6">
                        <Checkbox
                            v-if="editMode"
                            v-model="form.automatedMails"
                            :binary="true"
                            id="automatedMails"
                        />
                        <div v-else class="p-py-2 p-pl-2">
                            <span v-if="user.shouldReceiveAutomatedMails !== undefined">
                                {{ user.shouldReceiveAutomatedMails ? $t('yes') : $t('no') }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="p-field p-grid">
                    <label for="manualMails" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('shouldReceiveManualMails') }}</label>
                    <div class="p-col-6">
                        <Checkbox
                            v-if="editMode"
                            v-model="form.manualMails"
                            :binary="true"
                            id="manualMails"
                        />
                        <div v-else class="p-py-2 p-pl-2">
                            <span v-if="user.shouldReceiveManualMails !== undefined">
                                {{ user.shouldReceiveManualMails ? $t('yes') : $t('no') }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="p-field p-grid">
                    <label for="automatedChemicalMails" class="p-col-6 p-mb-2 p-mb-md-0">{{ $t('shouldReceiveAutomatedChemicalMails') }}</label>
                    <div class="p-col-6">
                        <Checkbox
                            v-if="editMode"
                            v-model="form.automatedChemicalMails"
                            :binary="true"
                            id="automatedChemicalMails"
                        />
                        <div v-else class="p-py-2 p-pl-2">
                            <span v-if="user.shouldReceiveAutomatedChemicalMails !== undefined">
                                {{ user.shouldReceiveAutomatedChemicalMails ? $t('yes') : $t('no') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

          <UserQualifications :user-id="userId" :allow-edit="canEditUser"></UserQualifications>
        </div>
    </div>
</template>

<script>
import UserQualifications from './UserQualifications.vue';
import UserService from '@/service/UserService.js';
import OrganizationService from '@/service/OrganizationService.js';
import YesNoDialog from '@/components/Shared/YesNoDialog.vue';
import { JwtToken } from '@/utils/jwt';

const EMAIL_REGEX =
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const EMPTY_SITE_PREFERENCE = { id: null, name: '---' };

export default {
    components: {
        UserQualifications,
        YesNoDialog
    },
    props: {
        userId: String
    },
    service: null,
    organizationService: null,
    data() {
        return {
            user: {},
            form: {},
            sitePreferencesOptions: [],
            accessTemplateOptions: [],
            editMode: false,
            displayEditConfirmation: false,
            errors: [],
            canEditUser: false,
        };
    },
    created() {
        this.service = new UserService();
        this.organizationService = new OrganizationService();
    },
    mounted() {

        this.canEditUser =
            Number(this.userId) === JwtToken.fromLocalStorage().userId()
            || JwtToken.fromLocalStorage().isSystemAdmin()
            || JwtToken.fromLocalStorage().associations().some(association => association.role === 'Admin');

        this.service.getAccessTemplates()
            .then(data => this.accessTemplateOptions = data);

        this.service.get(this.userId)
            .then(data => {
                this.user = data;

                this.form = {
                    name: data.name,
                    username: data.username,
                    email: data.email,
                    automatedMails: data.shouldReceiveAutomatedMails,
                    manualMails: data.shouldReceiveManualMails,
                    automatedChemicalMails: data.shouldReceiveAutomatedChemicalMails,
                    sitePreferences: data.sitePreferences || EMPTY_SITE_PREFERENCE,
                    accessTemplate: data.accessTemplate
                };

                this.sitePreferencesOptions = this.user
                    .accessTemplate
                    .allowedModules
                    .map(moduleAccess => moduleAccess.module)
                    .filter(module => !!module.postLoginDestinationUrl)
                    .sort((moduleA, moduleB) => moduleA.name.localeCompare(moduleB.name));
              })
              .catch(() => this.errors = [this.$t("somethingWentWrong")]);
    },
    methods: {
        editUser() {

            this.validateForm();

            const userSitePreferences = this.user.sitePreferences || EMPTY_SITE_PREFERENCE;
            const isUserChanged = this.form.name !== this.user.name
                               || this.form.username !== this.user.username
                               || this.form.email !== this.user.email
                               || this.form.automatedMails !== this.user.shouldReceiveAutomatedMails
                               || this.form.manualMails !== this.user.shouldReceiveManualMails
                               || this.form.automatedChemicalMails !== this.user.shouldReceiveAutomatedChemicalMails
                               || this.form.sitePreferences.id !== userSitePreferences.id
                               || this.form.accessTemplate.id !== this.user.accessTemplate.id;

            if (!this.errors.length) {
                if (isUserChanged) {
                    this.service.editUser(
                        this.userId,
                        {
                            name: this.form.name,
                            username: this.form.username,
                            email: this.form.email,
                            automatedMails: this.form.automatedMails,
                            manualMails: this.form.manualMails,
                            automatedChemicalMails: this.form.automatedChemicalMails,
                            sitePreferencesId: this.form.sitePreferences.id || null,
                            accessTemplateId: this.form.accessTemplate.id,
                        }
                    )
                        .then((user) => {
                            this.user = user
                            this.editMode = false;
                            this.errors = [];
                        })
                        .catch((errors) => {
                            const translatedErrors = errors.map(err => err.code)
                                .filter(Boolean)
                                .map(errorCode => this.$t(errorCode));

                            if (translatedErrors.length > 0) {
                                this.errors = translatedErrors;
                            } else {
                                this.errors = [this.$t("somethingWentWrong")];
                            }
                        })
                } else {
                    this.editMode = false;
                }
            }
        },
        returnToReadMode() {
            this.editMode = false;
            this.form = {
                name: this.user.name,
                username: this.user.username,
                email: this.user.email,
                automatedMails: this.user.shouldReceiveAutomatedMails,
                manualMails: this.user.shouldReceiveManualMails,
                automatedChemicalMails: this.user.shouldReceiveAutomatedChemicalMails,
                sitePreferences: this.user.sitePreferences || EMPTY_SITE_PREFERENCE,
                accessTemplate: this.user.accessTemplate
            };

            this.errors = [];
        },
        validateForm() {
            if (this.form.email && !EMAIL_REGEX.test(this.form.email)) {
                this.errors = [this.$t('InvalidEmailAddress')];
            } else {
                this.errors = [];
            }
        },
    }
}
</script>

<style lang="scss">
    @import '~bootstrap/scss/bootstrap';
</style>

<i18n lang="yaml" locale="en">
shouldReceiveAutomatedMails: "Receives automatically sent emails"
shouldReceiveManualMails: "Receives manually sent emails"
shouldReceiveAutomatedChemicalMails: "Receives chemical update emails"
postLoginDestination: "Post login destination"
UserAccess: "Insufficient permissions to edit user"
accessTemplate: "Access template"
</i18n>

<i18n lang="yaml" locale="dk">
searchChemicalsTitle: "Kemikalie information"
myChemicalsTooltip: "Kemikalier tilmeldt mnavigator"
myChemicals: "Mine kemikalier"
noChemicalsFound: "Intet kemikalie fundet!"
chemicalNameOrPartNumber: "Kemikalie navn eller part nr."
recentlyUpdated: "Nyeste Opdateret"
searchResults: "Søgeresultater"
shouldReceiveAutomatedMails: "Modtage automatisk udsendte emails"
shouldReceiveManualMails: "Modtage manuelt udsendte emails"
shouldReceiveAutomatedChemicalMails: "Modtage \"Kemi opdateringer\" mail"
postLoginDestination: null
accessTemplate: null
</i18n>
